import type { Product, Analytics } from '../types'
import { ProductCategories, ProductCategoryLabels } from '@esf/shared'

// Simple icon renderer
const renderIcon = (path: string, className = 'w-5 h-5') => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={path} />
  </svg>
)

// KPI Card component
const KpiCard = ({ title, value, icon, color, onClick }: { title: string; value: number | string; icon: string; color: string; onClick?: () => void }) => (
  <div
    className={`kpi ${onClick ? 'cursor-pointer hover:shadow-md hover:border-esf-navy/20 transition-all' : ''}`}
    onClick={onClick}
    role={onClick ? 'button' : undefined}
    tabIndex={onClick ? 0 : undefined}
    onKeyDown={onClick ? (e) => e.key === 'Enter' && onClick() : undefined}
  >
    <div className={`kpi-icon ${color}`}>
      {renderIcon(icon)}
    </div>
    <div className="kpi-title">{title}</div>
    <div className="kpi-value">{value}</div>
    {onClick && (
      <div className="text-xs text-gray-400 mt-1 flex items-center gap-1">
        View all {renderIcon('M9 5l7 7-7 7', 'w-3 h-3')}
      </div>
    )}
  </div>
)

// Category badge component
const CategoryBadge = ({ category }: { category: string }) => {
  const colors: Record<string, string> = {
    seating: 'bg-blue-100 text-blue-700',
    litter_recycling: 'bg-green-100 text-green-700',
    shelters: 'bg-purple-100 text-purple-700',
    lighting: 'bg-yellow-100 text-yellow-700',
    signage: 'bg-orange-100 text-orange-700',
    planters: 'bg-emerald-100 text-emerald-700',
    bollards_barriers: 'bg-red-100 text-red-700',
    cycle_parking: 'bg-cyan-100 text-cyan-700',
    ev_charging: 'bg-indigo-100 text-indigo-700',
    digital_displays: 'bg-pink-100 text-pink-700',
  }
  return (
    <span className={`badge ${colors[category] || 'bg-gray-100 text-gray-700'}`}>
      {ProductCategoryLabels[category as keyof typeof ProductCategoryLabels] || category}
    </span>
  )
}

// Status badge component
const StatusBadge = ({ status }: { status: string }) => {
  const colors: Record<string, string> = {
    ACTIVE: 'bg-success-100 text-success-700',
    INACTIVE: 'bg-gray-100 text-gray-500',
    MAINTENANCE: 'bg-warning-100 text-warning-700',
    REPAIR_NEEDED: 'bg-error-100 text-error-700',
    DECOMMISSIONED: 'bg-gray-100 text-gray-500',
  }
  return (
    <span className={`badge ${colors[status] || 'bg-gray-100 text-gray-700'}`}>
      {status.replace(/_/g, ' ')}
    </span>
  )
}

interface QuickLinkFilters {
  status?: string
  category?: string
}

interface DashboardPageProps {
  products: Product[]
  analytics: Analytics | null
  fetchProducts: () => void
  fetchAnalytics: () => void
  navigateTo: (page: string, id?: string) => void
  navigateWithFilters: (page: string, filters: QuickLinkFilters) => void
  setSelectedProduct: (product: Product) => void
  setProductDetailModalOpen: (v: boolean) => void
}

export default function DashboardPage({
  products,
  analytics,
  fetchProducts,
  fetchAnalytics,
  navigateTo,
  navigateWithFilters,
  setSelectedProduct,
  setProductDetailModalOpen,
}: DashboardPageProps) {
  return (
    <div className="container-page">
      {/* Co-brand hero — Ping Group × Thompson Aero × ubloquity */}
      <div className="mb-6 rounded-xl border border-gray-200 bg-gradient-to-r from-white via-white to-gray-50 p-5 shadow-sm">
        <div className="flex items-center gap-6 flex-wrap">
          <img src="/ping-logo.png" alt="Ping Group — Powered by Ubloquity" className="h-16 w-auto" />
          <div className="h-12 w-px bg-gray-200" />
          <div className="flex items-center gap-3">
            <img src="/thompson-aero-logo.png" alt="Thompson Aero Seating" className="h-12 w-12 object-contain" />
            <div className="leading-tight">
              <div className="text-xs uppercase tracking-wider text-gray-500">Showcased manufacturer</div>
              <div className="text-base font-semibold text-gray-900">Thompson Aero Seating</div>
              <div className="text-xs text-gray-500">Portadown · Northern Ireland</div>
            </div>
          </div>
          <div className="ml-auto text-right">
            <div className="text-xs uppercase tracking-wider text-gray-500">Digital Product Passport</div>
            <div className="text-sm font-medium text-gray-800">Aerospace cabin assets</div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <button onClick={() => { fetchProducts(); fetchAnalytics() }} className="btn btn-secondary">
          {renderIcon('M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15')}
          Refresh
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <KpiCard
          title="Total Products"
          value={analytics?.total || 0}
          icon="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
          color="bg-esf-navy/10 text-esf-navy"
          onClick={() => navigateWithFilters('assets', {})}
        />
        <KpiCard
          title="Active Products"
          value={analytics?.byStatus?.ACTIVE || 0}
          icon="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
          color="bg-success-100 text-success-600"
          onClick={() => navigateWithFilters('assets', { status: 'ACTIVE' })}
        />
        <KpiCard
          title="Maintenance Required"
          value={(analytics?.byStatus?.MAINTENANCE || 0) + (analytics?.byStatus?.REPAIR_NEEDED || 0)}
          icon="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          color="bg-warning-100 text-warning-600"
          onClick={() => navigateWithFilters('assets', { status: 'MAINTENANCE' })}
        />
        <KpiCard
          title="Carbon Footprint"
          value={`${((analytics?.totalCarbonFootprint || 0) / 1000).toFixed(1)}t`}
          icon="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          color="bg-info-100 text-info-600"
        />
      </div>

      {/* Products by Category */}
      <div className="card mb-8">
        <div className="card-header flex items-center justify-between">
          <h2 className="card-title">Products by Category</h2>
          <button onClick={() => navigateWithFilters('assets', {})} className="btn btn-sm btn-outline">View All</button>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {ProductCategories.filter(cat => (analytics?.byCategory?.[cat] || 0) > 0 || cat === 'seating' || cat === 'litter_recycling' || cat === 'shelters' || cat === 'bollards_barriers').slice(0, 5).map(cat => (
              <div
                key={cat}
                className="text-center p-4 rounded-lg bg-gray-50 cursor-pointer hover:bg-gray-100 hover:shadow-sm transition-all"
                onClick={() => navigateWithFilters('assets', { category: cat })}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && navigateWithFilters('assets', { category: cat })}
              >
                <CategoryBadge category={cat} />
                <div className="text-2xl font-bold mt-2">{analytics?.byCategory?.[cat] || 0}</div>
                <div className="text-xs text-gray-400 mt-1 flex items-center justify-center gap-1">
                  View {renderIcon('M9 5l7 7-7 7', 'w-3 h-3')}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent Products */}
      <div className="card">
        <div className="card-header flex items-center justify-between">
          <h2 className="card-title">Recent Products</h2>
          <button onClick={() => navigateTo('assets')} className="btn btn-sm btn-outline">View All</button>
        </div>
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Passport ID</th>
                <th>Category</th>
                <th>Status</th>
                <th>Subcategory</th>
              </tr>
            </thead>
            <tbody>
              {products.slice(0, 5).map(product => (
                <tr
                  key={product.id}
                  className="cursor-pointer hover:bg-gray-50"
                  onClick={() => {
                    setSelectedProduct(product)
                    setProductDetailModalOpen(true)
                  }}
                >
                  <td className="font-medium">{product.name}</td>
                  <td className="font-mono text-sm text-gray-500">{product.passportId}</td>
                  <td><CategoryBadge category={product.category} /></td>
                  <td><StatusBadge status={product.status} /></td>
                  <td className="text-sm text-gray-600">{product.subcategory || '-'}</td>
                </tr>
              ))}
              {products.length === 0 && (
                <tr>
                  <td colSpan={5} className="text-center py-8 text-gray-500">No products found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
